import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IconClose, IconMenu } from '@noths/polaris-client-ribbons-design-system';

import * as S from '../PhotoPicker.styles';

// Inline retry glyph — the design-system icon set has no refresh, and this
// is the only place we need it. `currentColor` lets the surrounding button
// own the colour.
const IconRefresh = () => (
  <svg fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.7681 4.23317C10.7716 3.2298 9.41565 2.66584 8.00147 2.6665C5.0548 2.6665 2.6748 5.05317 2.6748 7.99984C2.6748 10.9465 5.0548 13.3332 8.00147 13.3332C10.4881 13.3332 12.5615 11.6332 13.1548 9.33317H11.7681C11.2053 10.931 9.69556 11.9999 8.00147 11.9998C5.7948 11.9998 4.00147 10.2065 4.00147 7.99984C4.00147 5.79317 5.7948 3.99984 8.00147 3.99984C9.10814 3.99984 10.0948 4.45984 10.8148 5.18651L8.66814 7.33317H13.3348V2.6665L11.7681 4.23317Z"
      fill="currentColor"
    />
  </svg>
);

interface Props {
  deleteLabel: string;
  /** Committed URLs the consumer has flagged as errored. Rendered in the failed
   * visual state but WITHOUT a retry button — resolved by delete + re-add. */
  erroredUrls?: ReadonlySet<string>;
  /** URLs whose upload failed in the last attempt — rendered with a red
   * border and faded image so the customer can spot which tiles to retry
   * or remove. */
  failedUrls?: ReadonlySet<string>;
  onDelete: (url: string) => void;
  onReorder: (from: number, to: number) => void;
  /** Per-tile retry handler — clears the failed status for `url` so the
   * next Confirm re-attempts the upload. Required when `failedUrls` is
   * non-empty. */
  onRetry?: (url: string) => void;
  previewFor: (url: string) => string | undefined;
  retryLabel: string;
  urls: string[];
}

interface DragStart {
  height: number;
  idx: number;
  offsetX: number;
  offsetY: number;
  pointerId: number;
  width: number;
  x: number;
  y: number;
}

interface DragPreview {
  height: number;
  srcIdx: number;
  width: number;
}

// Pixels the pointer must travel before a press becomes a drag — keeps quick
// taps from briefly flashing the source tile as "dragging".
const DRAG_THRESHOLD_PX = 5;

const cursorTransform = (start: DragStart, x: number, y: number) =>
  `translate(${x - start.offsetX}px, ${y - start.offsetY}px)`;

export function PhotoPickerGrid({
  deleteLabel,
  erroredUrls,
  failedUrls,
  onDelete,
  onReorder,
  onRetry,
  previewFor,
  retryLabel,
  urls,
}: Props) {
  const [drag, setDrag] = useState<DragPreview | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const startRef = useRef<DragStart | null>(null);
  const dragOverIdxRef = useRef<number | null>(null);
  const onReorderRef = useRef(onReorder);
  const followerRef = useRef<HTMLDivElement>(null);
  const isReorderable = urls.length > 1;

  // Keep the latest onReorder reachable from the document-level listeners
  // without putting it in the effect deps (which would re-attach on every
  // parent render).
  onReorderRef.current = onReorder;

  const endDrag = useCallback(() => {
    startRef.current = null;
    dragOverIdxRef.current = null;
    setDrag(null);
    setDragOverIdx(null);
  }, []);

  // iOS Safari/Chrome don't fire HTML5 drag events from touch input, so we
  // run reorder off Pointer Events instead. Document-level listeners ensure
  // the drop still resolves if the finger leaves the grid or releases over
  // the dropzone below. The effect runs exactly twice per gesture (start
  // and end) — `dragOverIdx` lives in a ref so per-event updates don't
  // re-attach the listeners.
  useEffect(() => {
    if (drag == null) {
      return undefined;
    }
    const start = startRef.current;
    if (start == null) {
      return undefined;
    }
    const handleMove = (e: PointerEvent) => {
      if (e.pointerId !== start.pointerId) {
        return;
      }
      if (followerRef.current) {
        followerRef.current.style.transform = cursorTransform(start, e.clientX, e.clientY);
      }
      // iOS WebKit keeps `e.target` glued to the original tile for the full
      // touch gesture (implicit pointer capture that releasePointerCapture
      // doesn't reliably release), so hit-test by coordinates instead. The
      // e.target fallback is for jsdom, which doesn't implement
      // elementFromPoint.
      const hit = document.elementFromPoint?.(e.clientX, e.clientY) ?? (e.target as Element | null);
      const tileEl = (hit as HTMLElement | null)?.closest('[data-tile-idx]') as HTMLElement | null;
      if (tileEl == null) {
        return;
      }
      const overIdx = Number(tileEl.dataset.tileIdx);
      if (Number.isNaN(overIdx) || overIdx === dragOverIdxRef.current) {
        return;
      }
      dragOverIdxRef.current = overIdx;
      setDragOverIdx(overIdx);
    };
    const handleUp = (e: PointerEvent) => {
      if (e.pointerId !== start.pointerId) {
        return;
      }
      const overIdx = dragOverIdxRef.current;
      if (overIdx != null && overIdx !== start.idx) {
        onReorderRef.current(start.idx, overIdx);
      }
      endDrag();
    };
    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
    document.addEventListener('pointercancel', handleUp);
    return () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      document.removeEventListener('pointercancel', handleUp);
    };
  }, [drag, endDrag]);

  // Position the follower at the cursor on the first paint after the drag
  // becomes active, so it doesn't flash at the viewport origin before the
  // first pointermove.
  useLayoutEffect(() => {
    if (drag == null || followerRef.current == null || startRef.current == null) {
      return;
    }
    followerRef.current.style.transform = cursorTransform(
      startRef.current,
      startRef.current.x,
      startRef.current.y,
    );
  }, [drag]);

  const handlePointerDown = (i: number) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isReorderable) {
      return;
    }
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    // Touch input implicitly captures the pointer to the original element;
    // release it so events fire on whichever sibling tile the finger moves over.
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    const rect = e.currentTarget.getBoundingClientRect();
    startRef.current = {
      height: rect.height,
      idx: i,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      pointerId: e.pointerId,
      width: rect.width,
      x: e.clientX,
      y: e.clientY,
    };
  };

  const handleTilePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = startRef.current;
    if (start == null || drag != null || e.pointerId !== start.pointerId) {
      return;
    }
    if (Math.hypot(e.clientX - start.x, e.clientY - start.y) < DRAG_THRESHOLD_PX) {
      return;
    }
    dragOverIdxRef.current = start.idx;
    setDragOverIdx(start.idx);
    setDrag({ height: start.height, srcIdx: start.idx, width: start.width });
  };

  // Pre-threshold release on a tile = a tap, not a drag. Clear the latent
  // start so it doesn't leak into the next gesture. Post-threshold gestures
  // are owned by the document listener.
  const handleTilePointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (drag != null) {
      return;
    }
    if (startRef.current?.pointerId === e.pointerId) {
      startRef.current = null;
    }
  };

  return (
    <>
      <S.TileList role="list">
        {urls.map((url, i) => {
          const src = previewFor(url) ?? url;
          const isDragging = drag?.srcIdx === i;
          const isDropTarget = drag != null && dragOverIdx === i && drag.srcIdx !== i;
          const isFailed = failedUrls?.has(url) ?? false;
          const isErrored = erroredUrls?.has(url) ?? false;
          const showFailed = isFailed || isErrored;
          return (
            <S.Tile
              $dragOver={isDropTarget}
              $dragging={isDragging}
              $failed={showFailed}
              data-errored={isErrored}
              data-tile-idx={i}
              key={url}
              onPointerCancel={handleTilePointerEnd}
              onPointerDown={handlePointerDown(i)}
              onPointerMove={handleTilePointerMove}
              onPointerUp={handleTilePointerEnd}
              role="listitem"
            >
              <img alt="" draggable={false} src={src} />
              <S.DeleteButton
                $failed={showFailed}
                aria-label={deleteLabel}
                onClick={() => onDelete(url)}
                type="button"
              >
                <IconClose color="currentColor" height={16} width={16} />
              </S.DeleteButton>
              <S.TilePositionBadge aria-hidden="true">{i + 1}</S.TilePositionBadge>
              {isFailed ? (
                <S.TileRefreshButton
                  aria-label={retryLabel}
                  onClick={() => onRetry?.(url)}
                  type="button"
                >
                  <IconRefresh />
                </S.TileRefreshButton>
              ) : (
                isReorderable && (
                  <S.TileDragHandle aria-hidden="true">
                    <IconMenu height={16} width={16} />
                  </S.TileDragHandle>
                )
              )}
            </S.Tile>
          );
        })}
      </S.TileList>
      {drag != null && (
        <S.DragFollower
          aria-hidden="true"
          ref={followerRef}
          style={{ height: drag.height, width: drag.width }}
        >
          <img alt="" draggable={false} src={previewFor(urls[drag.srcIdx]) ?? urls[drag.srcIdx]} />
        </S.DragFollower>
      )}
    </>
  );
}
